import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  faCheck, faFileCheck, faFileEdit, faHourglass, faTachometer,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { hyphenCaseToSnakeCase } from '@domain-group/javascript-utils'

import type { UseFiltersPayload } from '@domain-group/react-hooks'

import useCurrentUser from '@hooks/useCurrentUser'
import useServices from '@hooks/useServices'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import type { ServiceModel } from '@models/service'

import useOrdersOverview from '../../hooks/useOrdersOverview'

import OrderItemsTable from '../OrderItemsTable'
import OrdersBlankState from '../OrdersBlankState'

const buildPageFilterFields = (services: ServiceModel[]) => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'campaigns',
      },
      key: 'campaign_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'updated_date_start',
      label: 'Start Date',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'updated_date_end',
      label: 'End Date',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'due_date_start',
      label: 'Due After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'due_date_end',
      label: 'Due Before',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Agent',
        searchRequestOptions: {
          actable_types: ['Agent'],
        },
      },
      key: 'userId',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      key: 'publication_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: () => ({ actable_types: ['OfficeAdministrator'] }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        label: 'Priority For',
        entityKey: 'users',
        entityTitleKey: 'full_name',
      },
      key: 'priority_orders_by_user_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: services.map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedExternalFilters, persistedFilters } = filterPayload

  // @ts-expect-error TS2339
  const { priority_orders_by_user_id: priorityCampaignUserId, userId } = pageFilters || {}

  const { currentUser } = useCurrentUser()
  const selectedPriorityUserId = priorityCampaignUserId || currentUser.id

  const users = useSelector(reduxState => reduxState.entities.users)

  useLocation()

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, updateFilters,
    },
    defaultFilters,
  } = pageContext

  // @ts-expect-error TS2339
  const statusFilter = persistedExternalFilters?.status || 'pending'

  const orderOverviewsPayload = useOrdersOverview({
    filters: {
      ...pageFilters,
      status_group_key: hyphenCaseToSnakeCase(statusFilter),
      agent_id: userId ? users[userId]?.actable_id : undefined,
    },
  })

  const {
    groupedOrderItems,
    hasFilteredOrderItems,
    loading,
  } = orderOverviewsPayload

  const { filteredServices } = useServices({ performHttpRequests: true })

  return (
    <>
      <Helmet>
        <title>Orders | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey={statusFilter}
        tabBarItems={[
          {
            icon: faTachometer,
            key: 'all',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'all' }),
            title: 'All Orders',
          },
          {
            icon: faHourglass,
            key: 'pending',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'pending' }),
            title: 'Pending',
          },
          {
            icon: faFileEdit,
            key: 'artwork-pending',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'artwork-pending' }),
            title: 'Artwork Pending',
          },
          {
            icon: faFileCheck,
            key: 'artwork-approved',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'artwork-approved' }),
            title: 'Artwork Approved',
          },
          {
            icon: faCheck,
            key: 'complete',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'complete' }),
            title: 'Completed',
          },
        ]}
        title="Orders"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.orders.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredOrderItems && <OrdersBlankState />}

            {!loading && hasFilteredOrderItems && Object.values(groupedOrderItems).map((group) => {
              if (group.orderItems.length){
                return (
                  <DashboardModule
                    contentBoxProps={{ flexDirection: 'column' }}
                    key={group.key}
                    title={group.title}
                  >
                    <OrderItemsTable orderItems={group.orderItems} selectedPriorityUserId={selectedPriorityUserId} />
                  </DashboardModule>
                )
              }

              return null
            })}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.orders.sidebar"
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(filteredServices)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
